/**
 * LotSmith — EA state banner.
 *
 * Surfaces non-NORMAL EA states (BLOCKED / TARGET_HIT / COOLDOWN / OVER_LIMIT / ALGO_OFF)
 * pushed up by the EA via `ea_state` events. Rendered above every account-
 * scoped route so a trader who jumps straight to /app/trades or /app/calendar
 * still sees that the EA is preventing new trades.
 *
 * The reason text comes from the EA (e.g. "Daily loss 5.2% > 5.0% cap"); when
 * it's missing we fall back to a per-state default. `since` shows how stale
 * the state is — important because a BLOCKED state from 6h ago means the
 * day rollover may already have unblocked the EA but the heartbeat hasn't
 * landed yet.
 */
import type { EaState } from "../providers/dataContext.js";

interface EaStateBannerProps {
  state: EaState;
  reason: string | null;
  since: string | null;
}

interface StateMeta {
  tone: "danger" | "warning" | "success";
  title: string;
  body: string;
}

const STATE_META: Record<Exclude<EaState, "NORMAL">, StateMeta> = {
  BLOCKED:    { tone: "danger",  title: "EA blocked",      body: "Daily loss limit hit. Trade open is blocked until tomorrow." },
  TARGET_HIT: { tone: "success", title: "Daily target hit", body: "Daily profit target reached. New entries are locked until reset." },
  COOLDOWN:   { tone: "warning", title: "EA in cooldown",  body: "Consecutive losses tripped the post-loss cooldown." },
  OVER_LIMIT: { tone: "warning", title: "Max open trades", body: "You're already at the configured open-positions limit." },
  ALGO_OFF:   { tone: "warning", title: "Algo trading off", body: "MT5's AutoTrading toolbar button is currently off." },
};

export function EaStateBanner({ state, reason, since }: EaStateBannerProps) {
  if (!state || state === "NORMAL") return null;
  const meta = STATE_META[state];
  return (
    <section className={`ea-banner ea-banner--${meta.tone}`} role="status">
      <span className="ea-banner__dot" aria-hidden />
      <div className="ea-banner__main">
        <div className="ea-banner__title">{meta.title}</div>
        <div className="ea-banner__body">
          {reason ?? meta.body}
          {since && (
            <span className="ea-banner__since"> · {formatSince(since)}</span>
          )}
        </div>
      </div>
    </section>
  );
}

function formatSince(iso: string): string {
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return "";
  const diffSec = Math.max(0, Math.floor((Date.now() - t) / 1000));
  if (diffSec < 60) return `${diffSec}s ago`;
  if (diffSec < 3600) return `${Math.floor(diffSec / 60)} min ago`;
  if (diffSec < 86400) return `${Math.floor(diffSec / 3600)}h ago`;
  return new Date(iso).toLocaleString("en-US", {
    month: "short", day: "numeric", hour: "2-digit", minute: "2-digit", hour12: false,
  });
}
